import React from "react";

const SITE_NAME = "Pozeman";
const SITE_URL = (import.meta.env.VITE_SITE_URL || "https://quality-b041a.firebaseapp.com").replace(/\/$/, "");
const DEFAULT_IMAGE = `${SITE_URL}/pozeman_icon.png`;

type SeoConfig = {
  title: string;
  description: string;
  keywords: string;
  path: string;
};

const byPath: Record<string, SeoConfig> = {
  "/": {
    title: "Pozeman",
    description:
      "Pozeman is a fast, local-first Postman alternative for API testing. Test REST APIs, manage collections, and collaborate with cleaner request workflows.",
    keywords:
      "Postman alternative, API testing tool, REST client online, API client, REST API testing, Pozeman",
    path: "/",
  },
  "/features": {
    title: "Pozeman Features | API Collections, Variables, and Testing Workflows",
    description:
      "Explore Pozeman features for API testing: collection management, global variables, environment switching, and fast response inspection.",
    keywords:
      "API testing features, Postman alternative features, API collections, environment variables",
    path: "/features",
  },
  "/blog": {
    title: "Pozeman Blog | API Testing Guides and Postman Alternative Tips",
    description:
      "Read actionable API testing tutorials, REST client best practices, and migration guides for teams moving from Postman.",
    keywords:
      "API testing blog, REST testing guide, Postman alternative guide",
    path: "/blog",
  },
};

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id: string, data: unknown) {
  let el = document.head.querySelector(`script#${id}`) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function SEO() {
  const pathname = typeof window === "undefined" ? "/" : window.location.pathname || "/";
  const config = byPath[pathname] || byPath["/"];
  const canonical = `${SITE_URL}${config.path}`;

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE_NAME,
    applicationCategory: "DeveloperApplication",
    operatingSystem: "Web",
    description: config.description,
    url: canonical,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  };

  React.useEffect(() => {
    document.title = config.title;
    upsertMeta("name", "description", config.description);
    upsertMeta("name", "keywords", config.keywords);
    upsertCanonical(canonical);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", config.title);
    upsertMeta("property", "og:description", config.description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", DEFAULT_IMAGE);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", config.title);
    upsertMeta("name", "twitter:description", config.description);
    upsertMeta("name", "twitter:image", DEFAULT_IMAGE);

    upsertJsonLd("pozeman-software-schema", softwareSchema);
  }, [canonical, config.description, config.keywords, config.title, softwareSchema]);

  return null;
}
